export const ADMIN_THEME_STORAGE_KEY = "crmrag_admin_theme";

export type AdminTheme = "light" | "dark";

function isAdminTheme(value: string | null): value is AdminTheme {
  return value === "light" || value === "dark";
}

function prefersDarkColorScheme(): boolean {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

/** Returns stored explicit preference, or null when following the OS (system mode). */
export function readStoredAdminTheme(): AdminTheme | null {
  try {
    const raw = localStorage.getItem(ADMIN_THEME_STORAGE_KEY);
    return isAdminTheme(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Must stay in sync with the inline script in client/index.html. */
export function resolveAdminTheme(): AdminTheme {
  const stored = readStoredAdminTheme();
  if (stored) return stored;
  return prefersDarkColorScheme() ? "dark" : "light";
}

export function writeAdminTheme(theme: AdminTheme): void {
  try {
    localStorage.setItem(ADMIN_THEME_STORAGE_KEY, theme);
  } catch {
    // ignore quota / private mode
  }
}

export function applyAdminTheme(theme: AdminTheme): void {
  if (theme === "dark") {
    document.documentElement.dataset.theme = "dark";
  } else {
    clearAdminThemeFromDom();
  }
}

export function clearAdminThemeFromDom(): void {
  delete document.documentElement.dataset.theme;
}

export function toggleAdminTheme(current: AdminTheme): AdminTheme {
  const next: AdminTheme = current === "dark" ? "light" : "dark";
  writeAdminTheme(next);
  applyAdminTheme(next);
  return next;
}
